/**
 * 导出选项组件
 * 用于显示单个导出格式选项
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { ExportFormat } from '../../types/export'
import { Icon } from '../ui/Icon'

interface ExportOptionProps {
  format: ExportFormat
  title: string
  description: string
  icon: string
  isDisabled?: boolean
  isSelected?: boolean
  onClick: () => void
}

/**
 * 导出格式信息映射
 */
const formatInfo: Record<ExportFormat, {
  icon: string
  color: string
}> = {
  pdf: {
    icon: 'document-text',
    color: 'text-red-600'
  },
  word: {
    icon: 'document',
    color: 'text-blue-600'
  },
  html: {
    icon: 'globe',
    color: 'text-orange-600'
  },
  png: {
    icon: 'photo',
    color: 'text-green-600'
  },
  jpeg: {
    icon: 'photo',
    color: 'text-purple-600'
  },
  svg: {
    icon: 'color-swatch',
    color: 'text-pink-600'
  },
  excel: {
    icon: 'chart-bar',
    color: 'text-emerald-600'
  },
  csv: {
    icon: 'table',
    color: 'text-cyan-600'
  },
  markdown: {
    icon: 'code',
    color: 'text-gray-600'
  },
  rtf: {
    icon: 'document-text',
    color: 'text-indigo-600'
  }
}

/**
 * 导出选项组件
 * 显示单个导出格式的卡片
 */
export const ExportOption: React.FC<ExportOptionProps> = ({
  format: _format,
  title,
  description,
  icon,
  isDisabled = false,
  isSelected = false,
  onClick
}) => {
  return (
    <div
      onClick={!isDisabled ? onClick : undefined}
      className={`
        relative p-4 border rounded-lg cursor-pointer transition-all duration-200
        ${isDisabled 
          ? 'opacity-50 cursor-not-allowed bg-gray-50' 
          : 'hover:border-blue-500 hover:shadow-md bg-white'
        }
        ${isSelected
          ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50'
          : 'border-gray-200'
        }
      `}
    >
      <div className="flex items-start space-x-3">
        <div className={`p-2 rounded-lg bg-gray-100 ${isDisabled ? 'text-gray-400' : ''}`}>
          <Icon name={icon} className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">
            {title}
          </h4>
          <p className="mt-1 text-xs text-gray-500 line-clamp-2">
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

interface ExportOptionGridProps {
  selectedFormat?: ExportFormat
  onFormatSelect: (format: ExportFormat) => void
  disabledFormats?: ExportFormat[]
}

/**
 * 导出选项网格
 * 显示所有可用的导出格式
 */
export const ExportOptionGrid: React.FC<ExportOptionGridProps> = ({
  selectedFormat,
  onFormatSelect,
  disabledFormats = []
}) => {
  const { t } = useTranslation()
  const formats = Object.keys(formatInfo) as ExportFormat[]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {formats.map((format) => {
        const info = formatInfo[format]
        const isDisabled = disabledFormats.includes(format)
        
        return (
          <ExportOption
            key={format}
            format={format}
            title={t(`export.formats.${format}.title`)}
            description={t(`export.formats.${format}.description`)}
            icon={info.icon}
            isDisabled={isDisabled}
            isSelected={selectedFormat === format}
            onClick={() => onFormatSelect(format)}
          />
        )
      })}
    </div>
  )
}